import * as apiFunction from '@services/apiFunctions'

import { API_URLS } from '@services/apiRoutes'

import type {
  GetPeoplePreviewDetailsRequestProps,
  PeoplePreviewDetailsApiResponseProps,
  DetailedSummaryResponseProps,
  DetailedSummaryApiResponseProps,
  EditDetailedSummaryPayloadProps,
  CollaboratorsDataProps,
  CollaboratorsListResponseProps,
  GetCollaboratorsListRequestProps,
  CollaboratorDetailsResponseProps,
  GetCollaboratorDetailsRequestProps,
  CollaboratorsTotalResponseProps,
  GetCollaboratorsTotalRequestProps,
  CollaboratorsIndicatorsResponseProps,
  GetCollaboratorsIndicatorsRequestProps,
  RescissionSimulationRequestProps,
  RescissionSimulationResponseProps,
  ProportionalSalaryRequestProps,
  ProportionalSalaryResponseProps,
  SimulationListItemProps,
  GetSimulationsApiResponseProps,
  SimulationCollaboratorProps,
  ProportionalSalaryRescissionRequestProps
} from '@pages/People/context/types'

export const getPeoplePreviewDetails = async (
  params: GetPeoplePreviewDetailsRequestProps
): Promise<PeoplePreviewDetailsApiResponseProps> => {
  const { filiCode, competenceDate, profitCenter } = params

  return await apiFunction.apiRequestWithQueryParams({
    url: `${API_URLS.finapi.peopleV2}/${encodeURIComponent(filiCode)}/detalhe-previa/dt-competencia/${encodeURIComponent(competenceDate)}`,
    method: 'GET',
    params: profitCenter ? { profitCenter } : undefined
  })
}

export const getDetailedSummary = async (
  params: GetPeoplePreviewDetailsRequestProps
): Promise<DetailedSummaryApiResponseProps> => {
  const { filiCode, competenceDate } = params

  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.peopleV2}/${encodeURIComponent(filiCode)}/busca-resumo-detalhado`,
    method: 'POST',
    body: { referenceDate: competenceDate }
  })
}

export const editDetailedSummary = async (
  payload: EditDetailedSummaryPayloadProps & GetPeoplePreviewDetailsRequestProps
): Promise<DetailedSummaryResponseProps> => {
  const { filiCode, competenceDate, ...data } = payload

  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.peopleV2}/${encodeURIComponent(filiCode)}/resumo-detalhado`,
    method: 'PUT',
    body: {
      referenceDate: competenceDate,
      accountingCode: data.accountingCode,
      accountingDescription: data.accountingDescription,
      amount: data.amount,
      isDiscount: data.isDiscount
    }
  })
}

export const getCollaborators = async (
  params: GetPeoplePreviewDetailsRequestProps
): Promise<CollaboratorsDataProps> => {
  const { filiCode, competenceDate } = params

  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.people}/${encodeURIComponent(filiCode)}/colaboradores`,
    method: 'POST',
    body: { referenceDate: competenceDate }
  })
}

export const getCollaboratorsList = async (
  params: GetCollaboratorsListRequestProps
): Promise<CollaboratorsListResponseProps> => {
  const {
    filiCode,
    referenceDate,
    currentPage = 1,
    itemsPerPage = 10,
    employeeName,
    employeeRegistration,
    positions,
    statuses,
    startDateFrom,
    sortBy,
    sortType,
    excludeWithTermination
  } = params

  const body: Record<string, unknown> = {
    referenceDate,
    currentPage,
    itemsPerPage
  }
  if (employeeName != null) body.employeeName = employeeName
  if (employeeRegistration != null) body.employeeRegistration = employeeRegistration
  if (positions != null && positions.length > 0) body.positions = positions
  if (statuses != null && statuses.length > 0) body.statuses = statuses
  if (startDateFrom != null && startDateFrom !== '') body.startDateFrom = startDateFrom
  if (sortBy != null && sortBy !== '') {
    body.sortBy = sortBy
    body.sortType = sortType ?? ''
  }

  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.peopleV2}/${encodeURIComponent(filiCode)}/busca-colaboradores`,
    method: 'POST',
    body,
    params: {
      excludeWithTermination
    }
  })
}

export const getCollaboratorDetails = async (
  params: GetCollaboratorDetailsRequestProps
): Promise<CollaboratorDetailsResponseProps> => {
  const { filiCode, employeeRegistration, referenceDate } = params

  return await apiFunction.apiRequestWithQueryParams({
    url: `${API_URLS.finapi.peopleV2}/${encodeURIComponent(filiCode)}/colaboradores/${encodeURIComponent(employeeRegistration)}/detalhes`,
    method: 'GET',
    params: { referenceDate }
  })
}

export interface UpdateCollaboratorCostPayloadProps {
  referenceDate: string // ISO format
  costOrigin: string
  accountCode: string
  amount: number
}

export const updateCollaboratorCost = async (
  filiCode: string,
  employeeRegistration: string,
  payload: UpdateCollaboratorCostPayloadProps
): Promise<void> => {
  await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.people}/${encodeURIComponent(filiCode)}/colaboradores/${encodeURIComponent(employeeRegistration)}/custos`,
    method: 'PUT',
    body: payload
  })
}

export const getCollaboratorsTotal = async (
  params: GetCollaboratorsTotalRequestProps
): Promise<CollaboratorsTotalResponseProps> => {
  const { filiCode, referenceDate } = params

  return await apiFunction.apiRequestWithQueryParams({
    url: `${API_URLS.finapi.people}/${encodeURIComponent(filiCode)}/colaboradores/total`,
    method: 'GET',
    params: { referenceDate }
  })
}

export const getCollaboratorsIndicators = async (
  params: GetCollaboratorsIndicatorsRequestProps
): Promise<CollaboratorsIndicatorsResponseProps> => {
  const { filiCode, referenceDate } = params

  return await apiFunction.apiRequestWithQueryParams({
    url: `${API_URLS.finapi.people}/${encodeURIComponent(filiCode)}/colaboradores/indicadores`,
    method: 'GET',
    params: { referenceDate }
  })
}

export const createRescissionSimulation = async (
  filiCode: string,
  payload: RescissionSimulationRequestProps
): Promise<RescissionSimulationResponseProps> => {
  const { referenceDate, ...body } = payload

  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.people}/${encodeURIComponent(filiCode)}/simulacao/rescisao`,
    method: 'POST',
    body,
    params: {
      referenceDate
    }
  })
}

export const calculateProportionalSalaryAdmission = async (
  filiCode: string,
  payload: ProportionalSalaryRequestProps
): Promise<ProportionalSalaryResponseProps> => {
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.people}/${encodeURIComponent(filiCode)}/calcular-salario-proporcional-admissao`,
    method: 'POST',
    body: payload
  })
}

export const calculateProportionalSalaryRescission = async (
  filiCode: string,
  payload: ProportionalSalaryRescissionRequestProps
): Promise<ProportionalSalaryResponseProps> => {
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.people}/${encodeURIComponent(filiCode)}/calcular-salario-proporcional-rescisao`,
    method: 'POST',
    body: payload
  })
}

export interface CalculateFgtsArt22RequestProps {
  salary: number
  startDate: string // yyyy-MM-dd
  terminationDate: string // yyyy-MM-dd
  noticeType: 'TRABALHADO' | 'INDENIZADO'
}

export interface CalculateFgtsArt22ResponseProps {
  fgtsAmount: number
  completeMonths: number
}

export const calculateFgtsArt22 = async (
  payload: CalculateFgtsArt22RequestProps
): Promise<CalculateFgtsArt22ResponseProps> => {
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.people}/calcular-fgts-art22`,
    method: 'POST',
    body: payload
  })
}

export interface CalculateNoticePeriodRequestProps {
  salary: number
  admissionDate: string // yyyy-MM-dd
  terminationDate: string // yyyy-MM-dd
  noticeType: 'TRABALHADO' | 'INDENIZADO'
}

export interface CalculateNoticePeriodResponseProps {
  noticePayment: number
}

export const calculateNoticePeriod = async (
  payload: CalculateNoticePeriodRequestProps
): Promise<CalculateNoticePeriodResponseProps> => {
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.people}/calcular-aviso-previo`,
    method: 'POST',
    body: payload
  })
}

export interface CalculateTotalEstimatedRequestProps {
  filiCode: string
  referenceDate: string
  employeeRegistration: string
  noticeType: string
  terminationDate: string
  salary: number
  manualFgtsArt22?: number
}

export interface CalculateTotalEstimatedResponseProps {
  totalEstimated: number
  balance: number
  salaries: number
}

export const calculateRescissionTotalEstimated = async (
  params: CalculateTotalEstimatedRequestProps
): Promise<CalculateTotalEstimatedResponseProps> => {
  const { filiCode, referenceDate, ...body } = params

  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.people}/${encodeURIComponent(filiCode)}/simulacao/rescisao/calcular-total-estimado`,
    method: 'POST',
    body: {
      employeeRegistration: body.employeeRegistration,
      noticeType: body.noticeType,
      terminationDate: body.terminationDate,
      salary: body.salary,
      manualFgtsArt22: body.manualFgtsArt22 ?? 0
    },
    params: { referenceDate }
  })
}

export const getSimulations = async (
  filiCode: string,
  referenceDate: string,
  currentPage: number = 1,
  itemsPerPage: number = 10
): Promise<GetSimulationsApiResponseProps> => {
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.people}/${encodeURIComponent(filiCode)}/busca-simulacoes`,
    method: 'POST',
    body: {
      referenceDate,
      currentPage,
      itemsPerPage
    }
  })
}

export const deleteSimulation = async (simulationId: number): Promise<void> => {
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.people}/simulacao/${simulationId}`,
    method: 'DELETE'
  })
}

export interface EditRescissionSimulationRequestProps {
  employeeRegistration: string
  noticeType: string
  terminationDate: string // yyyy-MM-dd format
  manualFgtsArt22?: number
  balance?: number
}

export interface EditAdmissionSimulationRequestProps {
  position: string
  startDate: string
  salary: number
}

export interface CreateAdmissionSimulationRequestProps {
  position: string
  startDate: string // yyyy-MM-dd format
  salary: number
  referenceDate: string // Query param: yyyy-MM-dd
}

export const createAdmissionSimulation = async (
  filiCode: string,
  payload: CreateAdmissionSimulationRequestProps
): Promise<SimulationListItemProps> => {
  const { referenceDate, ...body } = payload

  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.people}/${encodeURIComponent(filiCode)}/simulacao/admissao`,
    method: 'POST',
    body,
    params: {
      referenceDate
    }
  })
}

export const editRescissionSimulation = async (
  filiCode: string,
  simulationId: number,
  payload: EditRescissionSimulationRequestProps
): Promise<SimulationListItemProps> => {
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.people}/${encodeURIComponent(filiCode)}/simulacao/rescisao/${simulationId}`,
    method: 'PUT',
    body: payload
  })
}

export const editAdmissionSimulation = async (
  filiCode: string,
  simulationId: number,
  payload: EditAdmissionSimulationRequestProps
): Promise<SimulationListItemProps> => {
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.people}/${encodeURIComponent(filiCode)}/simulacao/admissao/${simulationId}`,
    method: 'PUT',
    body: payload
  })
}

export const getSimulationCollaborators = async (
  filiCode: string,
  referenceDate: string,
  name?: string
): Promise<SimulationCollaboratorProps[]> => {
  return await apiFunction.apiRequestWithQueryParams({
    url: `${API_URLS.finapi.people}/${encodeURIComponent(filiCode)}/simulacao/busca-colaboradores`,
    method: 'GET',
    params: {
      referenceDate,
      ...(name ? { Name: name } : {})
    }
  })
}

export interface ReleasePreviewPeopleRequestProps {
  filiCode: string
  competenceDate: string
}

export interface GeneralAdjustRequestProps {
  referenceDate: string
  entryDescription: string
  adjustmentDate: string
  totalAmount: number
  observations: string
}

export const generalAdjustPeople = async (
  filiCode: string,
  payload: GeneralAdjustRequestProps
): Promise<void> => {
  await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.people}/${encodeURIComponent(filiCode)}/ajuste-geral`,
    method: 'PUT',
    body: payload
  })
}

export interface GeneralAdjustHistoryRequestProps {
  /** Período (ex.: "P12"). Omitido = todas as competências do CL */
  period?: string
  /** Data competência (yyyy-MM-dd). Sem period e referenceDate = todas as competências do CL */
  referenceDate?: string
  currentPage: number
  itemsPerPage: number
}

export interface GeneralAdjustHistoryItemProps {
  id: number
  competencePeriod: string
  /** yyyy-MM-ddT00:00:00 */
  referenceDate: string
  /** Data do ajuste informada pelo usuário. Registros antigos vêm como "0001-01-01T00:00:00" (sem data) */
  adjustmentDate: string | null
  /** ISO 8601 (UTC, com "Z") */
  createdAt: string
  userName: string
  totalAmount: number
  observations: string
}

export interface GeneralAdjustHistoryResponseProps {
  items: GeneralAdjustHistoryItemProps[]
  currentPage: number
  itemsPerPage: number
  totalItems: number
  totalPages: number
  totalAmount: number
}

// Origem: PreviaResumoPessoal (Grupo "Ajustes Manuais", Origem "Ajuste Manual"), ordenado do mais recente p/ o mais antigo
export const getGeneralAdjustHistory = async (
  filiCode: string,
  params: GeneralAdjustHistoryRequestProps
): Promise<GeneralAdjustHistoryResponseProps> => {
  return await apiFunction.apiRequestWithQueryParams({
    url: `${API_URLS.finapi.people}/${encodeURIComponent(filiCode)}/ajuste-geral/historico`,
    method: 'GET',
    params
  })
}

export const releasePreviewPeople = async (payload: ReleasePreviewPeopleRequestProps) => {
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.peopleV2}/${payload.filiCode}/liberar-previa-semanal/dt-competencia/${payload.competenceDate}`,
    method: 'PATCH'
  })
}

export const reversePreviewPeople = async (payload: ReleasePreviewPeopleRequestProps) => {
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.peopleV2}/${payload.filiCode}/reverter-previa-semanal/dt-competencia/${payload.competenceDate}`,
    method: 'PATCH'
  })
}
